/**
 * Contratos de la API: esquemas de validacion y tipos derivados.
 *
 * El servidor valida cada cuerpo de peticion contra estos esquemas y el cliente
 * usa los mismos tipos, asi que un cambio de forma rompe la compilacion en los
 * dos lados a la vez en lugar de fallar en produccion.
 *
 * Regla de seguridad que atraviesa todo el archivo: ningun cuerpo de peticion
 * incluye participante_id. La identidad del participante sale siempre de la
 * cookie de sesion firmada que emite el servidor, nunca de datos que envie el
 * cliente. Ver docs/decisiones.md, decision 3.
 */

import { z } from 'zod';
import { IDIOMAS } from './idiomas.ts';

/* -------------------------------------------------------------------------- */
/* Piezas reutilizables                                                        */
/* -------------------------------------------------------------------------- */

export const esquemaIdioma = z.enum(IDIOMAS);

/** Texto traducible: al menos el idioma por defecto tiene que venir relleno. */
export const esquemaTextoMultiidioma = z
  .object({
    ca: z.string().trim().max(5000).optional(),
    es: z.string().trim().max(5000).optional(),
    en: z.string().trim().max(5000).optional(),
  })
  .refine((valor) => Object.values(valor).some((texto) => texto && texto.trim() !== ''), {
    message: 'Hace falta el texto al menos en un idioma',
  });

export const esquemaLatitud = z.number().min(-90).max(90);
export const esquemaLongitud = z.number().min(-180).max(180);

export const esquemaCoordenada = z.object({
  lat: esquemaLatitud,
  lon: esquemaLongitud,
});

/* -------------------------------------------------------------------------- */
/* Registro de participantes                                                   */
/* -------------------------------------------------------------------------- */

export const esquemaRegistro = z.object({
  nombre: z.string().trim().min(2).max(120),
  responsable: z.string().trim().min(2).max(120),
  telefono: z
    .string()
    .trim()
    .min(6)
    .max(30)
    // Permisivo a proposito: acepta prefijos internacionales, espacios y guiones.
    // Validar formatos nacionales de telefono es una fuente inagotable de falsos
    // negativos y aqui el telefono solo sirve para contactar si hay incidencia.
    .regex(/^[+()\d][\d\s().-]*$/u, 'Formato de telefono no valido'),
  num_personas: z.number().int().min(1).max(50),
  email: z.email().trim().toLowerCase().max(254),
  idioma: esquemaIdioma,
  /**
   * Consentimiento explicito para el tratamiento de los datos de contacto.
   * Tiene que ser true de forma activa: una casilla premarcada no es
   * consentimiento valido.
   */
  consentimiento_datos: z.literal(true),
});

export type DatosRegistro = z.infer<typeof esquemaRegistro>;

export interface RespuestaRegistro {
  participante_id: number;
  /** Se informa de si el correo salio, para poder avisar al usuario si fallo. */
  email_enviado: boolean;
}

/* -------------------------------------------------------------------------- */
/* Sesion del participante                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Reenvio del QR de un equipo desde el panel.
 *
 * El destino es un valor cerrado y no una direccion libre: el panel no puede
 * servir para mandar el codigo de acceso de un equipo a cualquier buzon. O va
 * al correo con el que se inscribio el equipo, o al del administrador que lo
 * pide, que sale de su sesion y tampoco viaja en la peticion.
 */
export const esquemaReenvioQr = z.object({
  destino: z.enum(['participante', 'administrador']),
});

export type DestinoReenvioQr = z.infer<typeof esquemaReenvioQr>['destino'];

export interface RespuestaReenvioQr {
  /** Direccion a la que ha salido, para poder confirmarla en pantalla. */
  enviado_a: string;
  destino: DestinoReenvioQr;
}

export const esquemaCanjeSesion = z.object({
  /** Token opaco que viaja dentro del QR personal. */
  qr_token: z.uuid(),
});

export interface SesionParticipante {
  participante_id: number;
  nombre: string;
  evento: {
    id: number;
    nombre: string;
    fecha_hora_inicio: string;
    fecha_hora_fin: string;
    /** Calculado en servidor: si ahora mismo cae dentro de la franja horaria. */
    en_curso: boolean;
  };
}

/* -------------------------------------------------------------------------- */
/* Puntos                                                                      */
/* -------------------------------------------------------------------------- */

export interface PuntoPublico {
  id: number;
  nombre: string;
  descripcion: string;
  lat: number;
  lon: number;
  radio_metros: number;
  video_url: string | null;
  /** Texto alternativo del video, obligatorio por accesibilidad. */
  video_descripcion: string;
  /** Pista de subtitulos en el idioma resuelto, si el administrador la cargo. */
  subtitulos_url: string | null;
}

/* -------------------------------------------------------------------------- */
/* Visitas                                                                     */
/* -------------------------------------------------------------------------- */

export const esquemaVisita = z.object({
  punto_id: z.number().int().positive(),
  lat: esquemaLatitud,
  lon: esquemaLongitud,
  /**
   * Precision declarada por el navegador, en metros. El servidor la usa para
   * ampliar el margen de tolerancia: con mala cobertura el radio efectivo
   * crece, en vez de rechazar visitas legitimas.
   */
  precision_metros: z.number().min(0).max(10000).optional(),
});

export type DatosVisita = z.infer<typeof esquemaVisita>;

export interface RespuestaVisita {
  /** true solo la primera vez. Es lo que dispara el video automatico. */
  primera_visita: boolean;
  punto_id: number;
  /** Si con esta visita ya se cumplen las condiciones para ver la pregunta. */
  pregunta_disponible: boolean;
}

/* -------------------------------------------------------------------------- */
/* Preguntas y respuestas                                                      */
/* -------------------------------------------------------------------------- */

export interface OpcionPregunta {
  id: string;
  texto: string;
}

export interface PreguntaPublica {
  evento_punto_id: number;
  punto_id: number;
  enunciado: string;
  opciones: OpcionPregunta[];
  puntos_otorgados: number;
  /** Si ya respondio, se devuelve el resultado en lugar del formulario. */
  ya_respondida: boolean;
  resultado: ResultadoRespuesta | null;
}

export const esquemaRespuesta = z.object({
  evento_punto_id: z.number().int().positive(),
  respuesta_dada: z.string().trim().min(1).max(120),
});

export type DatosRespuesta = z.infer<typeof esquemaRespuesta>;

export interface ResultadoRespuesta {
  correcta: boolean;
  respuesta_dada: string;
  respuesta_correcta: string;
  puntos_obtenidos: number;
  /** Se muestran siempre, acierte o falle (regla de negocio 4). */
  explicacion_base: string;
  explicacion_ampliada: string;
  /** true si esta respuesta ya estaba guardada y se devuelve el resultado previo. */
  repetida: boolean;
}

/* -------------------------------------------------------------------------- */
/* Ranking                                                                     */
/* -------------------------------------------------------------------------- */

export interface FilaRanking {
  posicion: number;
  participante_id: number;
  nombre: string;
  puntos: number;
  aciertos: number;
  respuestas: number;
  /** Segundos entre la primera y la ultima respuesta. null con menos de dos. */
  duracion_segundos: number | null;
}

export interface Ranking {
  evento: { id: number; nombre: string; en_curso: boolean };
  filas: FilaRanking[];
  /** Momento de calculo, en hora de servidor, para poder mostrarlo. */
  calculado_en: string;
}

/* -------------------------------------------------------------------------- */
/* Errores                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Codigos de error de negocio. El cliente los traduce a un mensaje en el idioma
 * activo, asi que el servidor nunca manda texto de interfaz al usuario.
 */
export const CODIGOS_ERROR = [
  'sesion_no_valida',
  'sesion_requerida',
  'evento_no_encontrado',
  'evento_no_activo',
  'evento_fuera_de_franja',
  'evento_terminado',
  'registro_cerrado',
  'punto_no_encontrado',
  'punto_no_pertenece_al_evento',
  'visita_no_registrada',
  'demasiado_lejos',
  'pregunta_no_disponible',
  'respuesta_ya_enviada',
  'opcion_no_valida',
  'datos_no_validos',
  'credenciales_no_validas',
  'no_autorizado',
  'demasiadas_peticiones',
  /**
   * Direccion que no corresponde a ninguna ruta. Existe como codigo propio
   * porque antes el 404 generico devolvia 'evento_no_encontrado', y eso mandaba
   * a buscar el fallo al evento cuando el problema era la direccion.
   */
  'ruta_no_encontrada',
  /** El correo no se pudo entregar al servidor de salida. */
  'correo_no_enviado',
  /**
   * El participante ya no tiene datos de contacto: se anonimizaron al cumplirse
   * el plazo de retencion. La fila se conserva para el ranking, pero no hay
   * ninguna direccion a la que escribir.
   */
  'participante_anonimizado',
  'error_interno',
] as const;

export type CodigoError = (typeof CODIGOS_ERROR)[number];

export interface ErrorApi {
  error: CodigoError;
  /** Detalle tecnico, solo para depuracion y registro. No se muestra al usuario. */
  detalle?: string;
  /** Datos utiles para el mensaje traducido, por ejemplo la distancia que falta. */
  contexto?: Record<string, string | number>;
}
